import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { formatPercentage } from '../../../core/utils/common/formatters';
import { chartColours } from '../../../core/constants/colours.constants';
import { RankingItem } from '../../../core/types/common.types';
import useTheme from '@mui/material/styles/useTheme';

interface AgeVerticalBarChartProps {
  items: RankingItem[];
}

function AgeVerticalBarChart({ items }: AgeVerticalBarChartProps) {
  const theme = useTheme();
  const maxValue = Math.max(...items.map((item) => item.value));

  return (
    <Box>
      <Box
        sx={{
          height: { xs: 260, sm: 300 },
          display: 'flex',
          alignItems: 'flex-end',
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 0.5, sm: 1 },
          pt: 2,
          pb: 1,
          borderLeft: 1,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent 0,
            transparent 58px,
            ${theme.palette.grey[100]} 59px,
            ${theme.palette.grey[100]} 60px
          )`,
        }}
      >
        {items.map((item, index) => {
          const height = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
          const barColour = chartColours[index % chartColours.length];

          return (
            <Tooltip key={item.label} title={`${item.label}: ${formatPercentage(item.value)} of participants`}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Typography
                  variant="body2"
                  component="p"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '0.85rem', sm: '1rem', md: '1.08rem' },
                    color: barColour,
                    lineHeight: 1,
                  }}
                >
                  {formatPercentage(item.value)}
                </Typography>

                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 70,
                    height: `${height}%`,
                    minHeight: item.value > 0 ? 8 : 0,
                    borderRadius: '10px 10px 0 0',
                    bgcolor: barColour,
                    transition: 'height 300ms ease',
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          px: { xs: 0.5, sm: 1 },
          mt: 1,
        }}
      >
        {items.map((item) => (
          <Typography
            key={item.label}
            variant="body2"
            component="p"
            align="center"
            sx={{
              flex: 1,
              minWidth: 0,
              fontWeight: 800,
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              color: 'text.primary',
              lineHeight: 1.15,
              wordBreak: 'break-word',
            }}
          >
            {item.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default AgeVerticalBarChart;
